'use client';

import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { DayPicker } from 'react-day-picker';
import {
  Calendar,
  Close,
} from '@stadionhq-v3/scottishfa-web-tokens/dist/components/icons';
import dayjs from '@lib/dayjs';
import type { DateRange } from 'react-day-picker';
import { DateRangePickerProps } from './dateRangePicker.types';
import styles from './dateRangePicker.module.scss';

const DATE_FORMAT = 'DD MMM YYYY';

// dayjs is locale-scoped in this project; 'en' is the isolation default.
const formatDate = (date: Date) => dayjs('en')(date).format(DATE_FORMAT);

const formatRange = (range?: DateRange): string | null => {
  if (!range?.from) {
    return null;
  }
  if (!range.to) {
    return formatDate(range.from);
  }
  return `${formatDate(range.from)} – ${formatDate(range.to)}`;
};

const DateRangePicker = ({
  value,
  onChange,
  placeholder = 'Select dates',
  numberOfMonths,
  startLabel = 'Choose Start Date',
  endLabel = 'Choose End Date',
  classNames = [],
  nativeProps = {},
  testId = 'date-range-picker',
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false);
  // Uncontrolled fallback so the component works standalone (e.g. in a story).
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(
    undefined
  );

  const isControlled = value !== undefined || onChange !== undefined;
  const selected = isControlled ? value : internalRange;

  // One month on mobile, two on desktop (>= l breakpoint, 1024px), unless the
  // caller forces a fixed count via `numberOfMonths`.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const months = numberOfMonths ?? (isDesktop ? 2 : 1);

  const rootRef = useRef<HTMLDivElement>(null);

  const handleSelect = (range: DateRange | undefined) => {
    if (!isControlled) {
      setInternalRange(range);
    }
    onChange?.(range);
  };

  // Close on outside-click / Escape while the popover is open.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const label = formatRange(selected);
  // react-day-picker sets `{ from, to: from }` on the first click, so a
  // single-day selection means the user still needs to pick the end date.
  const choosingEnd = Boolean(
    selected?.from &&
    selected?.to &&
    dayjs('en')(selected.from).isSame(selected.to, 'day')
  );

  return (
    <div
      {...nativeProps}
      ref={rootRef}
      data-testid={testId}
      className={classnames(styles.dateRangePicker, classNames)}
    >
      <button
        type="button"
        className={styles.dateRangePicker__field}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={classnames(styles.dateRangePicker__value, {
            [styles['dateRangePicker__value--placeholder'] ?? '']: !label,
          })}
        >
          {label ?? placeholder}
        </span>
        <Calendar
          className={styles.dateRangePicker__icon}
          aria-hidden="true"
          focusable="false"
        />
      </button>

      {open && (
        <div
          className={styles.dateRangePicker__popover}
          role="dialog"
          aria-label="Choose date range"
          data-testid={`${testId}-popover`}
        >
          {/*
            react-day-picker's base stylesheet is imported globally in
            `src/app/layout.tsx` (Next.js allows global CSS only in a layout);
            the story imports it separately for isolation. Brand overrides live
            in the SCSS module under `:global(.rdp-*)` selectors.
          */}
          <div className={styles.dateRangePicker__header}>
            <span className={styles.dateRangePicker__heading}>
              {choosingEnd ? endLabel : startLabel}
            </span>
            <button
              type="button"
              className={styles.dateRangePicker__close}
              aria-label="Close"
              data-testid={`${testId}-close`}
              onClick={() => setOpen(false)}
            >
              <Close aria-hidden="true" focusable="false" />
            </button>
          </div>
          <DayPicker
            mode="range"
            numberOfMonths={months}
            selected={selected}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
